import { useEffect, useState, useCallback } from "react";
import { RefreshCw, X } from "lucide-react";

/**
 * UpdateAvailableBanner — small, unobtrusive Torahya-styled banner
 * that appears ONLY when a fresh service worker is sitting in
 * "waiting" state with new app code ready to activate.
 *
 * UX rules (per user spec):
 *   • Hebrew, Torahya identity (navy bg, gold accent).
 *   • Shows only when there's a real update — never on first install.
 *   • One-tap "רענן" → tells the waiting SW to activate; the page
 *     auto-reloads once (handled in `registerServiceWorker.js` via
 *     the `controllerchange` listener).
 *   • Dismissable with × in case the user wants to finish reading
 *     something first. The banner reappears on next page load if
 *     the update is still pending.
 *
 * Event contract:
 *   Listens to `window` event `torahya-sw-update-ready`. The detail
 *   payload is `{ worker: ServiceWorker }`. See registerServiceWorker
 *   for where it's dispatched.
 */
export default function UpdateAvailableBanner() {
  const [worker, setWorker] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    function onUpdate(e) {
      const w = e?.detail?.worker;
      if (w) setWorker(w);
    }
    window.addEventListener("torahya-sw-update-ready", onUpdate);
    return () =>
      window.removeEventListener("torahya-sw-update-ready", onUpdate);
  }, []);

  const handleRefresh = useCallback(() => {
    if (!worker || refreshing) return;
    setRefreshing(true);
    // Tell the waiting worker to take over. `controllerchange` in
    // registerServiceWorker.js will then trigger one window.reload().
    try {
      worker.postMessage({ type: "SKIP_WAITING" });
    } catch {
      // If postMessage fails for any reason, fall back to a hard reload
      // — the new SW will take over next page load anyway.
      window.location.reload();
    }
  }, [worker, refreshing]);

  if (!worker || dismissed) return null;

  return (
    <div
      dir="rtl"
      role="status"
      aria-live="polite"
      data-testid="sw-update-banner"
      className="
        fixed z-[60]
        inset-x-3 bottom-3
        sm:inset-x-auto sm:end-4 sm:bottom-4
        sm:max-w-sm
      "
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div
        className="
          flex items-center gap-3
          bg-[#1a2b4a] text-[#f6e9c5]
          border border-[#c9a449]/60
          rounded-full
          shadow-[0_18px_45px_-15px_rgba(26,43,74,0.55)]
          px-3 py-2
        "
      >
        <RefreshCw
          className="h-4 w-4 text-[#c9a449] shrink-0"
          strokeWidth={1.6}
        />
        <div className="text-xs sm:text-sm flex-1 min-w-0">
          <strong className="font-heading">עדכון זמין</strong>
          <span className="text-[#f6e9c5]/80 mx-1">·</span>
          <span className="text-[#f6e9c5]/80">לחצו כדי לרענן</span>
        </div>

        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="sw-update-banner-refresh"
          className="
            bg-[#c9a449] text-[#1a2b4a]
            hover:bg-[#d4b25c]
            disabled:opacity-60 disabled:cursor-wait
            text-xs font-heading font-medium
            rounded-full px-3 py-1.5
            transition-colors
            shrink-0
          "
        >
          {refreshing ? "מרענן..." : "רענן"}
        </button>

        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="סגור"
          data-testid="sw-update-banner-dismiss"
          className="
            h-7 w-7 rounded-full
            text-[#f6e9c5]/60 hover:text-[#f6e9c5]
            inline-flex items-center justify-center
            shrink-0
          "
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.6} />
        </button>
      </div>
    </div>
  );
}
